import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Store } from '@ngxs/store';
import { BroadcastService, EditableConfigGridData, EditableGridComponent, ObjectUtils, ToasterType } from '@cv-invision/core';
import { GridColumnsModel } from '../model/grid-columns.model';
import { OnHoldReasonsGridColumnsModel } from '../model/on-hold-reasons-grid-columns.model';
import { ICellRendererParams } from 'ag-grid-community';
import { MapperService } from './service/mapper.service';
import { ActivatedRoute } from '@angular/router';
import { INTEGRATIONS } from '../../model/global-configuration.constant';

@Component({
	selector: 'cv-invision-ux-mapper-types',
	templateUrl: './mapper-types.component.html',
	styleUrls: ['./mapper-types.component.scss'],
	providers: [MapperService]
})
export class MapperTypesComponent extends EditableGridComponent implements OnInit, OnDestroy {
	private atComponentDestroy = new Subject();
	saving: boolean;
	isDirtyForm: boolean;
	routeUrl: string;
	moduleName: string;
	labelHeaderName: string;
	appScopes: string;
	acuities: string;
	cmpAcuities: string;
	formFieldType: string;
	entity: string;

	constructor(private store: Store,
		private broadcastService: BroadcastService,
		private mapperService: MapperService,
		private route: ActivatedRoute) {
		super(mapperService);
	}

	ngOnInit(): void {
		this.route.data.subscribe(data => {
			this.entity = data.entity;
			this.mapperService.setEntity(this.entity);
			this.routeUrl      = data.config.urlApi;
			this.appScopes     = data.config.appScopes;
			this.acuities      = data.config.acuities;
			this.cmpAcuities      = data.config.cmpAcuities;
			this.formFieldType = data.config.formFieldType;
		});

		this.mapperService
			.getFormDataListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((result: EditableConfigGridData) => {
				if (result.doSave !== undefined && result.doSave) {
					this.onSave();
				}

				if (result.isDirty !== undefined) {
					this.isDirtyForm = result.isDirty;
				}
				if (result.saving !== undefined) {
					this.saving = result.saving;
				}
			});

		if (this.routeUrl === 'onhold-reasons') {
			this.gridOptions.columnDefs = new OnHoldReasonsGridColumnsModel(this.entity).getColumns();
		} else {
			const appScopes = this.store.selectSnapshot((state: any) => ObjectUtils.getStateFromString(state, this.appScopes));
			const acuities = this.store.selectSnapshot((state: any) => ObjectUtils.getStateFromString(state, this.acuities));
			const cmpAcuities = this.store.selectSnapshot((state: any) => ObjectUtils.getStateFromString(state, this.cmpAcuities));

			this.gridOptions.columnDefs = new GridColumnsModel(this.entity).getColumns(appScopes, acuities, cmpAcuities, this.formFieldType);
		}

		this.loadGridData();
	}

	/**
	 * Get config key
	 */
	getConfigKeys(): any {
		let configKeys: any = [];
		switch (this.entity) {
			case INTEGRATIONS.NEXSYS:
			case INTEGRATIONS.NEXSYS_DOCUMENT_TYPES:
				configKeys = {
					id: 'nexsysId',
					name: 'nexsysName'
				};

				if (this.routeUrl === 'onhold-reasons'){
					configKeys = {
						id: 'nexsysReason',
						name: 'nexsysReason'
					};
				}
				break;
			case INTEGRATIONS.VALUTRUST:
			case INTEGRATIONS.XPANSE:
			case INTEGRATIONS.FNC:
				configKeys = {
					id: 'id',
					name: 'name'
				};

				if (this.routeUrl === 'onhold-reasons'){
					configKeys = {
						id: 'fncReason',
						name: 'fncReason'
					};
				}
				break;
			default:
				break;
		}
		return configKeys;
	}


	/**
	 * Save current configuration.
	 */
	onSave(): void {
		this.mapperService.setFormData({ saving: true, doSave: false });

		let currentConfig: any = [];

		if (this.routeUrl === 'onhold-reasons') {
			currentConfig = this.config;
		} else {
			const configKeys = this.getConfigKeys();
			currentConfig = this.config.map(item => ({
				[configKeys.id]: item[configKeys.id],
				[configKeys.name]: item[configKeys.name],
				acuityId: item.acuityId,
				cmpAcuityId: item.cmpAcuityId,
				cmpAcuityName: item.cmpAcuityName,
				appScopeName: this.entity === INTEGRATIONS.NEXSYS_DOCUMENT_TYPES ?  item.appScopeName : null,
				appScopeId: this.entity === INTEGRATIONS.NEXSYS_DOCUMENT_TYPES ?  item.appScopeId : null,
				scopeId: this.entity !== INTEGRATIONS.NEXSYS_DOCUMENT_TYPES ?  item.scopeId : null,
			}));
		}

		this.mapperService
			.saveConfiguration(currentConfig, this.routeUrl)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.loadGridData();
				if (result && result.succeeded) {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: { message: 'Changes saved successfully' }
					});
				}
				this.mapperService.setFormData({ saving: false, doSave: false });
			}, error => {
				console.error(error);
				this.broadcastService.broadcast({
					name: ToasterType.ERROR,
					event: { error: 'Error occurred ' + error }
				});
				this.mapperService.setFormData({ saving: false, doSave: false });
			});
	}

	/**
	 * On cell updated change value of configuration and emit dirty change.
	 */
	onCellUpdated(params: ICellRendererParams, newData: any): void {
		const configKeys = this.getConfigKeys();
		//const id = params.data[configKeys.id];
		const match = this.config[params.rowIndex];
		if (match) {
			Object.assign(match, newData);
		}
		this.emitDirtyness();
	}

	/**
	 * Load grid data implementation to load the property types.
	 */
	loadGridData(): void {
		this.mapperService
			.getConfiguration(this.routeUrl)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.originalConfig = JSON.parse(JSON.stringify(result));
				this.config = result;
				this.gridData = this.config;
				this.gridPagination.pagination.limit = result.length;
				this.gridPagination.pagination.totalCount = result.length;
				this.gridHeight = this.getGridHeight(result && result.length > 0 ? result.length : 0);
			});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
